/**
 * Standard 7-card poker hand evaluator.
 *
 * `evaluate7` returns a single comparable integer: larger is always a
 * strictly stronger hand. The value packs the hand category (0 = high
 * card .. 8 = straight flush) plus up to five rank tiebreakers into one
 * integer so that plain numeric comparison (`>`, `<`, `===`) is sufficient
 * to compare any two hands.
 *
 * This module knows nothing about the joker — it is a plain evaluator over
 * concrete card integers. Joker interpretation lives in `joker.ts`, which
 * builds concrete 7-card hands (by substituting a candidate real card for
 * the joker) and calls into this evaluator.
 */

import { rankOf, suitOf, type Card } from "./cards.js";

export const HAND_CATEGORY = {
  HIGH_CARD: 0,
  ONE_PAIR: 1,
  TWO_PAIR: 2,
  THREE_OF_A_KIND: 3,
  STRAIGHT: 4,
  FLUSH: 5,
  FULL_HOUSE: 6,
  FOUR_OF_A_KIND: 7,
  STRAIGHT_FLUSH: 8,
} as const;

export type HandCategory = (typeof HAND_CATEGORY)[keyof typeof HAND_CATEGORY];

const CATEGORY_NAMES = [
  "high-card",
  "one-pair",
  "two-pair",
  "three-of-a-kind",
  "straight",
  "flush",
  "full-house",
  "four-of-a-kind",
  "straight-flush",
];

export function categoryName(category: number): string {
  return CATEGORY_NAMES[category] ?? `unknown(${category})`;
}

function encode(
  category: number,
  k1 = 0,
  k2 = 0,
  k3 = 0,
  k4 = 0,
  k5 = 0
): number {
  return (
    (category << 20) |
    (k1 << 16) |
    (k2 << 12) |
    (k3 << 8) |
    (k4 << 4) |
    k5
  );
}

/** Extracts the hand category (0..8) from an evaluate5/evaluate7 result. */
export function categoryOf(value: number): number {
  return (value >> 20) & 0xf;
}

// Scratch buffers reused across calls to avoid per-call allocation on the
// hot path. Safe because evaluation is synchronous and never re-entrant
// within a single call (evaluate7 fully finishes each evaluate5 call
// before starting the next).
const rankCountsScratch = new Int8Array(13);
const suitCountsScratch = new Int8Array(4);
const ranksPresentScratch = new Int8Array(13);
const groupRankScratch = new Int8Array(13);
const groupCountScratch = new Int8Array(13);

/**
 * Evaluates exactly 5 concrete cards and returns a comparable integer
 * (higher = stronger). See module doc for encoding.
 */
export function evaluate5(cards: readonly Card[]): number {
  const rankCounts = rankCountsScratch;
  const suitCounts = suitCountsScratch;
  rankCounts.fill(0);
  suitCounts.fill(0);

  for (let i = 0; i < 5; i++) {
    const c = cards[i]!;
    rankCounts[rankOf(c)]!++;
    suitCounts[suitOf(c)]!++;
  }

  let flushSuit = -1;
  for (let s = 0; s < 4; s++) {
    if (suitCounts[s] === 5) {
      flushSuit = s;
      break;
    }
  }

  // Distinct ranks present, descending.
  let numPresent = 0;
  for (let r = 12; r >= 0; r--) {
    if (rankCounts[r]! > 0) {
      ranksPresentScratch[numPresent++] = r;
    }
  }

  let straightHigh = -1;
  if (numPresent === 5) {
    if (ranksPresentScratch[0]! - ranksPresentScratch[4]! === 4) {
      straightHigh = ranksPresentScratch[0]!;
    } else if (
      ranksPresentScratch[0] === 12 &&
      ranksPresentScratch[1] === 3 &&
      ranksPresentScratch[2] === 2 &&
      ranksPresentScratch[3] === 1 &&
      ranksPresentScratch[4] === 0
    ) {
      // Wheel: A-2-3-4-5, high card is the 5 (rank index 3).
      straightHigh = 3;
    }
  }

  if (flushSuit !== -1 && straightHigh !== -1) {
    return encode(HAND_CATEGORY.STRAIGHT_FLUSH, straightHigh);
  }

  // Build (rank, count) groups sorted by count desc, then rank desc.
  let numGroups = 0;
  for (let i = 0; i < numPresent; i++) {
    const r = ranksPresentScratch[i]!;
    groupRankScratch[numGroups] = r;
    groupCountScratch[numGroups] = rankCounts[r]!;
    numGroups++;
  }
  // Simple insertion sort (numGroups <= 5): stable-ish, fine at this size.
  for (let i = 1; i < numGroups; i++) {
    const r = groupRankScratch[i]!;
    const c = groupCountScratch[i]!;
    let j = i - 1;
    while (
      j >= 0 &&
      (groupCountScratch[j]! < c ||
        (groupCountScratch[j] === c && groupRankScratch[j]! < r))
    ) {
      groupRankScratch[j + 1] = groupRankScratch[j]!;
      groupCountScratch[j + 1] = groupCountScratch[j]!;
      j--;
    }
    groupRankScratch[j + 1] = r;
    groupCountScratch[j + 1] = c;
  }

  const g0r = groupRankScratch[0]!;
  const g0c = groupCountScratch[0]!;
  const g1r = groupRankScratch[1]!;
  const g1c = groupCountScratch[1]!;

  if (g0c === 4) {
    return encode(HAND_CATEGORY.FOUR_OF_A_KIND, g0r, g1r);
  }
  if (g0c === 3 && g1c === 2) {
    return encode(HAND_CATEGORY.FULL_HOUSE, g0r, g1r);
  }
  if (flushSuit !== -1) {
    return encode(
      HAND_CATEGORY.FLUSH,
      ranksPresentScratch[0]!,
      ranksPresentScratch[1]!,
      ranksPresentScratch[2]!,
      ranksPresentScratch[3]!,
      ranksPresentScratch[4]!
    );
  }
  if (straightHigh !== -1) {
    return encode(HAND_CATEGORY.STRAIGHT, straightHigh);
  }
  if (g0c === 3) {
    return encode(HAND_CATEGORY.THREE_OF_A_KIND, g0r, g1r, groupRankScratch[2]!);
  }
  if (g0c === 2 && g1c === 2) {
    return encode(HAND_CATEGORY.TWO_PAIR, g0r, g1r, groupRankScratch[2]!);
  }
  if (g0c === 2) {
    return encode(
      HAND_CATEGORY.ONE_PAIR,
      g0r,
      g1r,
      groupRankScratch[2]!,
      groupRankScratch[3]!
    );
  }
  return encode(
    HAND_CATEGORY.HIGH_CARD,
    ranksPresentScratch[0]!,
    ranksPresentScratch[1]!,
    ranksPresentScratch[2]!,
    ranksPresentScratch[3]!,
    ranksPresentScratch[4]!
  );
}

// All C(7,5) = 21 index combinations, precomputed to avoid allocation on
// the hot path.
const COMBOS_7_CHOOSE_5: readonly (readonly number[])[] = (() => {
  const combos: number[][] = [];
  const idx = [0, 1, 2, 3, 4, 5, 6];
  const pick = [0, 0, 0, 0, 0];
  function rec(start: number, depth: number) {
    if (depth === 5) {
      combos.push([...pick]);
      return;
    }
    for (let i = start; i < idx.length; i++) {
      pick[depth] = idx[i]!;
      rec(i + 1, depth + 1);
    }
  }
  rec(0, 0);
  return combos;
})();

const combo5Scratch: Card[] = [0, 0, 0, 0, 0];

/**
 * Reference/cross-check implementation of the 7-card evaluator: tries
 * all C(7,5) = 21 five-card combinations via `evaluate5` and takes the
 * max. Correct but ~20x slower than `evaluate7`; kept around purely so
 * a property test can validate the fast implementation against it. Not
 * used on any hot path.
 */
export function evaluate7Combinatorial(cards: readonly Card[]): number {
  if (cards.length !== 7) {
    throw new RangeError(`evaluate7Combinatorial requires exactly 7 cards, got ${cards.length}`);
  }
  let best = -1;
  for (let i = 0; i < COMBOS_7_CHOOSE_5.length; i++) {
    const combo = COMBOS_7_CHOOSE_5[i]!;
    combo5Scratch[0] = cards[combo[0]!]!;
    combo5Scratch[1] = cards[combo[1]!]!;
    combo5Scratch[2] = cards[combo[2]!]!;
    combo5Scratch[3] = cards[combo[3]!]!;
    combo5Scratch[4] = cards[combo[4]!]!;
    const value = evaluate5(combo5Scratch);
    if (value > best) best = value;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Fast direct 7-card evaluator (hot path)
// ---------------------------------------------------------------------------

const rank7Counts = new Int32Array(13);
const suit7RankMask = new Int32Array(4);
const suit7Counts = new Int32Array(4);
const g7Rank = new Int32Array(7);
const g7Count = new Int32Array(7);
const top5Scratch = new Int32Array(5);

/** Highest straight (5 consecutive set bits) present in a 13-bit rank mask, or -1. Returns the *high* rank index of the straight. */
function straightHighFromMask(mask: number): number {
  // Ace-high down through 6-high (top bit of the 5-window is rank `top`).
  for (let top = 12; top >= 4; top--) {
    const window = 0b11111 << (top - 4);
    if ((mask & window) === window) return top;
  }
  // Wheel: A-2-3-4-5 (ranks 12,0,1,2,3), high card is the 5 (rank index 3).
  const wheel = (1 << 12) | (1 << 0) | (1 << 1) | (1 << 2) | (1 << 3);
  if ((mask & wheel) === wheel) return 3;
  return -1;
}

/** Fills `top5Scratch` with the top `n` (<=5) set-bit ranks in `mask`, descending; unfilled slots are 0. */
function fillTopRanksFromMask(mask: number, n: number): void {
  let found = 0;
  for (let r = 12; r >= 0 && found < n; r--) {
    if (mask & (1 << r)) {
      top5Scratch[found++] = r;
    }
  }
  for (; found < 5; found++) top5Scratch[found] = 0;
}

/**
 * Evaluates the best 5-card hand out of exactly 7 concrete cards. Returns
 * a comparable integer (higher = stronger); see module doc for encoding.
 *
 * Implementation note: rather than enumerating all C(7,5)=21 five-card
 * combinations (which is what `evaluate5` does per-combo), this computes
 * rank/suit histograms once and classifies directly — an order of
 * magnitude faster, which matters because `evaluateJoker` calls this in
 * a hot loop. Cross-checked against `evaluate7Combinatorial` by a
 * property test (see evaluator.test.ts).
 */
export function evaluate7(cards: readonly Card[]): number {
  if (cards.length !== 7) {
    throw new RangeError(`evaluate7 requires exactly 7 cards, got ${cards.length}`);
  }

  rank7Counts.fill(0);
  suit7RankMask.fill(0);
  suit7Counts.fill(0);
  let rankMask = 0;

  for (let i = 0; i < 7; i++) {
    const c = cards[i]!;
    const r = c >> 2;
    const s = c & 3;
    rank7Counts[r]!++;
    suit7Counts[s]!++;
    suit7RankMask[s]! |= 1 << r;
    rankMask |= 1 << r;
  }

  let best = -1;

  // Flush / straight flush.
  let flushSuit = -1;
  for (let s = 0; s < 4; s++) {
    if (suit7Counts[s]! >= 5) {
      flushSuit = s;
      break;
    }
  }
  if (flushSuit !== -1) {
    const sfHigh = straightHighFromMask(suit7RankMask[flushSuit]!);
    if (sfHigh !== -1) {
      // Straight flush is the maximum possible category; nothing can beat it.
      return encode(HAND_CATEGORY.STRAIGHT_FLUSH, sfHigh);
    }
    fillTopRanksFromMask(suit7RankMask[flushSuit]!, 5);
    const flushValue = encode(
      HAND_CATEGORY.FLUSH,
      top5Scratch[0]!,
      top5Scratch[1]!,
      top5Scratch[2]!,
      top5Scratch[3]!,
      top5Scratch[4]!
    );
    if (flushValue > best) best = flushValue;
  }

  // Rank-count groups, sorted by (count desc, rank desc).
  let n = 0;
  for (let r = 12; r >= 0; r--) {
    const c = rank7Counts[r]!;
    if (c > 0) {
      g7Rank[n] = r;
      g7Count[n] = c;
      n++;
    }
  }
  for (let i = 1; i < n; i++) {
    const r = g7Rank[i]!;
    const c = g7Count[i]!;
    let j = i - 1;
    while (j >= 0 && (g7Count[j]! < c || (g7Count[j] === c && g7Rank[j]! < r))) {
      g7Rank[j + 1] = g7Rank[j]!;
      g7Count[j + 1] = g7Count[j]!;
      j--;
    }
    g7Rank[j + 1] = r;
    g7Count[j + 1] = c;
  }

  const g0r = g7Rank[0]!;
  const g0c = g7Count[0]!;
  const g1r = n > 1 ? g7Rank[1]! : 0;
  const g1c = n > 1 ? g7Count[1]! : 0;

  // IMPORTANT: kickers must be the highest *remaining ranks by rank
  // order*, regardless of how many cards of that rank exist (e.g. with
  // 7 cards, "quads + a lower pair + an even lower single" must use the
  // single's rank as the kicker if it out-ranks the leftover pair — the
  // count-sorted group list is only valid for picking the primary
  // structure, not for kickers). So kickers are re-derived from the raw
  // rank bitmask, excluding whichever ranks form the primary structure.
  if (g0c === 4) {
    fillTopRanksFromMask(rankMask & ~(1 << g0r), 1);
    const v = encode(HAND_CATEGORY.FOUR_OF_A_KIND, g0r, top5Scratch[0]!);
    if (v > best) best = v;
  } else if (g0c === 3 && g1c >= 2) {
    // g1 may itself be another trip (two-trips case, e.g. AAA KKK Q):
    // the best full house uses g0 as trips and only 2 of g1's cards as
    // the pair.
    const v = encode(HAND_CATEGORY.FULL_HOUSE, g0r, g1r);
    if (v > best) best = v;
  } else if (g0c === 3) {
    fillTopRanksFromMask(rankMask & ~(1 << g0r), 2);
    const v = encode(HAND_CATEGORY.THREE_OF_A_KIND, g0r, top5Scratch[0]!, top5Scratch[1]!);
    if (v > best) best = v;
  } else if (g0c === 2 && g1c === 2) {
    fillTopRanksFromMask(rankMask & ~((1 << g0r) | (1 << g1r)), 1);
    const v = encode(HAND_CATEGORY.TWO_PAIR, g0r, g1r, top5Scratch[0]!);
    if (v > best) best = v;
  } else if (g0c === 2) {
    fillTopRanksFromMask(rankMask & ~(1 << g0r), 3);
    const v = encode(
      HAND_CATEGORY.ONE_PAIR,
      g0r,
      top5Scratch[0]!,
      top5Scratch[1]!,
      top5Scratch[2]!
    );
    if (v > best) best = v;
  } else {
    // All singles.
    fillTopRanksFromMask(rankMask, 5);
    const v = encode(
      HAND_CATEGORY.HIGH_CARD,
      top5Scratch[0]!,
      top5Scratch[1]!,
      top5Scratch[2]!,
      top5Scratch[3]!,
      top5Scratch[4]!
    );
    if (v > best) best = v;
  }

  // Straight (non-flush).
  const straightHigh = straightHighFromMask(rankMask);
  if (straightHigh !== -1) {
    const v = encode(HAND_CATEGORY.STRAIGHT, straightHigh);
    if (v > best) best = v;
  }

  return best;
}

export interface HandDetail {
  readonly value: number;
  readonly category: number;
  /** The concrete 5 cards making up the best hand. */
  readonly bestFive: readonly Card[];
}

/**
 * Like `evaluate7`, but also returns which concrete 5 cards form the
 * winning hand (not just the comparable integer). This is the
 * "explain the result" counterpart used for UI/showdown display (e.g.
 * "you made trip 7s using 7h 7d 7c"); it is not on the hot path, so it
 * favors simplicity/reuse over raw speed: it tries all C(7,5)=21
 * combinations via the already-tested `evaluate5` and keeps the best.
 */
export function evaluate7WithBestHand(cards: readonly Card[]): HandDetail {
  if (cards.length !== 7) {
    throw new RangeError(`evaluate7WithBestHand requires exactly 7 cards, got ${cards.length}`);
  }
  let bestValue = -1;
  let bestFive: Card[] = [];
  for (let i = 0; i < COMBOS_7_CHOOSE_5.length; i++) {
    const combo = COMBOS_7_CHOOSE_5[i]!;
    const hand = [
      cards[combo[0]!]!,
      cards[combo[1]!]!,
      cards[combo[2]!]!,
      cards[combo[3]!]!,
      cards[combo[4]!]!,
    ];
    const value = evaluate5(hand);
    if (value > bestValue) {
      bestValue = value;
      bestFive = hand;
    }
  }
  return { value: bestValue, category: categoryOf(bestValue), bestFive };
}

/**
 * Evaluates the best 5-card hand out of an arbitrary number (>=5) of
 * concrete cards. Slower, general-purpose version used for hand sizes
 * other than exactly 7 (e.g. flop/turn partial boards). Not on the hot
 * path.
 */
export function evaluateBest(cards: readonly Card[]): number {
  if (cards.length < 5) {
    throw new RangeError(`evaluateBest requires at least 5 cards, got ${cards.length}`);
  }
  if (cards.length === 5) return evaluate5(cards);
  if (cards.length === 7) return evaluate7(cards);

  let best = -1;
  const n = cards.length;
  const pick = [0, 0, 0, 0, 0];
  function rec(start: number, depth: number): void {
    if (depth === 5) {
      const hand = [
        cards[pick[0]!]!,
        cards[pick[1]!]!,
        cards[pick[2]!]!,
        cards[pick[3]!]!,
        cards[pick[4]!]!,
      ];
      const value = evaluate5(hand);
      if (value > best) best = value;
      return;
    }
    for (let i = start; i < n; i++) {
      pick[depth] = i;
      rec(i + 1, depth + 1);
    }
  }
  rec(0, 0);
  return best;
}
